import { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Button,
  Statistic,
  Alert,
  Timeline,
  Space,
  message,
  Modal,
  Input,
  Select,
} from "antd";
import {
  FileTextOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  EyeOutlined,
  FolderOpenOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { caseAPI, deadlineAPI, documentAPI, db1995API } from "../services/api";
import { Case } from "../types";
import dayjs from "dayjs";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copyingCaseId, setCopyingCaseId] = useState<number | null>(null);
  const [db1995Visible, setDb1995Visible] = useState(false);
  const [db1995Rows, setDb1995Rows] = useState<any[]>([]);
  const [db1995Loading, setDb1995Loading] = useState(false);
  const [db1995CaseId, setDb1995CaseId] = useState<number | undefined>(undefined);
  const [db1995OnlyIndexes, setDb1995OnlyIndexes] = useState(true);
  const [db1995Empresa, setDb1995Empresa] = useState<
    "all" | "ENDE" | "ENTEL" | "ENFE" | "LAB" | "YPFB" | "VINTO"
  >("all");
  const [db1995Drafts, setDb1995Drafts] = useState<Record<number, { index_name: string; reference_title: string }>>({});
  const [savingDb1995Id, setSavingDb1995Id] = useState<number | null>(null);

  function inferEmpresaFromFile(fileName?: string) {
    const v = (fileName || "").trim();
    const m = v.match(/^([aA][1-6])/);
    if (!m) return "UNKNOWN";
    const code = m[1].toUpperCase();
    const map: Record<string, string> = {
      A1: "ENDE",
      A2: "ENTEL",
      A3: "ENFE",
      A4: "LAB",
      A5: "YPFB",
      A6: "VINTO",
    };
    return map[code] || "UNKNOWN";
  }

  function inferReferenceTitleFromFile(fileName?: string) {
    const v = (fileName || "").trim();
    if (!v) return "";

    const letterMap: Record<string, string> = {
      B: "PROCESO DE PRIVATIZACION EN GENERAL",
      C: "REFORMA DEL SISTEMA DE FONDOS DE PENSIONES",
      D: "PROCESO DE REFORMA DEL SECTOR ELECTRICO",
      E: "FIDEICOMISO Y CUSTODIO GLOBAL",
      F: "PROCESO DE CONCESION MAESTRA DE LOS AEROPUERTOS",
      G: "CONCESION DE AGUAS",
      H: "FINANCIERO - CONTABLE",
      J: "SISTEMA DE REGULACION SECTORIAL (SIRESE)",
      O: "AUDITORIAS EXTERNA - AUDITEX",
    };

    // A1..A6 have company-specific titles.
    const aCompanyMap: Record<string, string> = {
      A1: "PROCESO DE CAPITALIZACION ENDE",
      A2: "PROCESO DE CAPITALIZACION ENTEL",
      A3: "PROCESO DE CAPITALIZACION ENFE",
      A4: "PROCESO DE CAPITALIZACION LAB",
      A5: "PROCESO DE CAPITALIZACION YPFB",
      A6: "PROCESO DE CAPITALIZACION VINTO",
    };

    // Also infer from the filename prefix (first letter).
    // This supports file sets like A..., B..., C..., etc.
    const prefixStem = v.replace(/\.[^.]+$/, ""); // remove extension
    const aPrefixMatch = prefixStem.match(/^([Aa][1-6])/);
    if (aPrefixMatch) {
      const key = aPrefixMatch[1].toUpperCase();
      if (aCompanyMap[key]) return aCompanyMap[key];
    }
    const prefixLetterMatch = prefixStem.match(/^([A-Za-z])[0-9]/);
    if (prefixLetterMatch) {
      const prefixLetter = prefixLetterMatch[1].toUpperCase();
      if (letterMap[prefixLetter]) return letterMap[prefixLetter];
    }

    // Get suffix between last "_" and the extension.
    // Example: A3V10_7.pdf -> suffix "7"
    // Example: A3V10_I.pdf -> suffix "I"
    const m = v.match(/_([^_]+)\.[^.]+$/);
    if (!m) return "";
    const suffix = (m[1] || "").trim();

    // If suffix is a letter like _B.pdf -> map directly.
    const upperSuffix = suffix.toUpperCase();
    if (aCompanyMap[upperSuffix]) return aCompanyMap[upperSuffix];
    if (letterMap[upperSuffix]) return letterMap[upperSuffix];

    // Otherwise, assume suffix is numeric representing the topic order in the index:
    // Based on the user-provided list in order:
    // A(1..6), B, C, D, E, F, G, H, J, O.
    const numeric = Number(suffix);
    const byOrder: Record<number, string> = {
      1: aCompanyMap.A1,
      2: aCompanyMap.A2,
      3: aCompanyMap.A3,
      4: aCompanyMap.A4,
      5: aCompanyMap.A5,
      6: aCompanyMap.A6,
      7: letterMap.B,
      8: letterMap.C,
      9: letterMap.D,
      10: letterMap.E,
      11: letterMap.F,
      12: letterMap.G,
      13: letterMap.H,
      14: letterMap.J,
      15: letterMap.O,
    };

    if (numeric && byOrder[numeric]) return byOrder[numeric];
    return "";
  }

  const getFilteredDb1995Rows = () => {
    const base = db1995OnlyIndexes
      ? db1995Rows.filter((r: any) => !!r.is_index_file)
      : db1995Rows;
    if (!db1995Empresa || db1995Empresa === "all") return base;
    return base.filter((r: any) => inferEmpresaFromFile(r.original_file_name) === db1995Empresa);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesData, deadlinesData] = await Promise.all([
        caseAPI.getAll(),
        deadlineAPI.getUpcoming(14),
      ]);
      setCases(casesData);
      setUpcomingDeadlines(deadlinesData.upcoming_deadlines || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: "blue",
      closed: "green",
      appealed: "orange",
    };
    return colors[status] || "default";
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      urgent: "red",
      high: "orange",
      medium: "blue",
      low: "green",
    };
    return colors[priority] || "default";
  };

  const caseColumns = [
    {
      title: "Número de Caso",
      dataIndex: "case_number",
      key: "case_number",
      render: (text: string, record: Case) => (
        <Button type="link" onClick={() => navigate(`/case/${record.case_id}`)}>
          {text}
        </Button>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "case_type",
      key: "case_type",
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Prioridad",
      dataIndex: "priority",
      key: "priority",
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>{priority.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Fecha de Presentación",
      dataIndex: "filing_date",
      key: "filing_date",
      render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: Case) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/case/${record.case_id}`)}
          >
            Ver
          </Button>
          <Button
            icon={<DatabaseOutlined />}
            loading={copyingCaseId === record.case_id}
            onClick={async () => {
              setCopyingCaseId(record.case_id);
              try {
                const res = await documentAPI.copyCasePdfsToDb1995(record.case_id);
                message.success(
                  `Caso ${record.case_number}: ${res.new_rows} nuevos, ${res.updated_rows} actualizados en db1995`
                );
              } catch (e: any) {
                message.error(
                  e?.response?.data?.detail || "Error al copiar PDFs a db1995"
                );
              } finally {
                setCopyingCaseId(null);
              }
            }}
          >
            db1995
          </Button>
        </Space>
      ),
    },
  ];

  const activeCases = cases.filter((c) => c.status === "active").length;
  const urgentDeadlines = upcomingDeadlines.filter(
    (d) => d.urgency === "urgent"
  ).length;

  const loadDb1995Rows = async () => {
    setDb1995Loading(true);
    try {
      const rows = await db1995API.list(undefined, 200000, db1995CaseId);
      setDb1995Rows(rows || []);
      const drafts: Record<number, { index_name: string; reference_title: string }> = {};
      (rows || []).forEach((row: any) => {
        const inferredRefTitle = inferReferenceTitleFromFile(row.original_file_name);
        drafts[row.id] = {
          index_name: row.index_name || "",
          // Keep saved DB value when present; otherwise infer from filename.
          reference_title: row.reference_title || inferredRefTitle,
        };
      });
      setDb1995Drafts(drafts);
    } catch (e: any) {
      message.error(
        e?.response?.data?.detail || "No se pudo cargar resultados de db1995"
      );
    } finally {
      setDb1995Loading(false);
    }
  };

  return (
    <div>
      <h1>Dashboard - Gestión de Casos Legales</h1>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Casos Activos"
              value={activeCases}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Casos"
              value={cases.length}
              prefix={<FolderOpenOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Plazos Próximos"
              value={upcomingDeadlines.length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Urgentes"
              value={urgentDeadlines}
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#f5222d" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Your Current Case Alert */}
      <Alert
        message="Caso Actual: FUD 701102022500681"
        description="Allanamiento de Domicilio y Asociación Delictuosa - Memorial de objeción presentado el 29/09/2025"
        type="info"
        showIcon
        icon={<FileTextOutlined />}
        action={
          <Button
            size="small"
            type="primary"
            onClick={() => navigate("/case/1")}
          >
            Ver Detalles
          </Button>
        }
        style={{ marginBottom: 24 }}
      />

      {/* Upcoming Deadlines */}
      {upcomingDeadlines.length > 0 && (
        <Card
          title="⏰ Plazos Próximos a Vencer"
          style={{ marginBottom: 24 }}
          extra={<Button type="link">Ver Todos</Button>}
        >
          <Timeline
            items={upcomingDeadlines.slice(0, 5).map((deadline) => ({
              color: deadline.urgency === "urgent" ? "red" : "blue",
              children: (
                <div>
                  <div style={{ fontWeight: "bold" }}>
                    {deadline.deadline_type} - Caso {deadline.case_number}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    Vence: {dayjs(deadline.due_date).format("DD/MM/YYYY")} (
                    {deadline.days_until} días)
                  </div>
                  <div style={{ fontSize: "12px" }}>{deadline.description}</div>
                </div>
              ),
            }))}
          />
        </Card>
      )}

      {/* Cases Table */}
      <Card
        title="📁 Mis Casos"
        extra={
          <Space>
            <Button
              onClick={async () => {
                setDb1995Visible(true);
                if (!db1995CaseId && cases.length > 0) {
                  setDb1995CaseId(cases[0].case_id);
                }
                await loadDb1995Rows();
              }}
            >
              Ver resultados db1995
            </Button>
            <Button type="primary" onClick={() => navigate("/new-case")}>
              + Nuevo Caso
            </Button>
          </Space>
        }
      >
        <Table
          columns={caseColumns}
          dataSource={cases}
          rowKey="case_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
      <Modal
        title="Resultados db1995"
        open={db1995Visible}
        onCancel={() => setDb1995Visible(false)}
        footer={null}
        width={2000}
      >
        <div style={{ marginBottom: 12 }}>
          <Space wrap>
            <span style={{ color: "#555" }}>Filtrar por caso:</span>
            <Select
              allowClear
              placeholder="Todos los casos"
              style={{ minWidth: 260 }}
              value={db1995CaseId}
              onChange={async (val) => {
                setDb1995CaseId(val);
                await loadDb1995Rows();
              }}
              options={cases.map((c) => ({
                value: c.case_id,
                label: `${c.case_number} (#${c.case_id})`,
              }))}
            />
            <Select
              value={db1995OnlyIndexes ? "only_indexes" : "all"}
              style={{ minWidth: 210 }}
              onChange={(val) => setDb1995OnlyIndexes(val === "only_indexes")}
              options={[
                { value: "only_indexes", label: "Solo indices (*_I / *_i)" },
                { value: "all", label: "Todos los archivos" },
              ]}
            />
            <Select
              value={db1995Empresa}
              style={{ minWidth: 230 }}
              onChange={(val) => setDb1995Empresa(val as any)}
              options={[
                { value: "all", label: "Todas las empresas" },
                { value: "ENDE", label: "ENDE (A1...)" },
                { value: "ENTEL", label: "ENTEL (A2...)" },
                { value: "ENFE", label: "ENFE (A3...)" },
                { value: "LAB", label: "LAB (A4...)" },
                { value: "YPFB", label: "YPFB (A5...)" },
                { value: "VINTO", label: "VINTO (A6...)" },
              ]}
            />
            <Button onClick={loadDb1995Rows}>Recargar</Button>
            <Button
              onClick={() => {
                const rows = getFilteredDb1995Rows();
                const toOpen = rows.slice(0, 20);
                if (rows.length === 0) {
                  message.info("No hay índices para abrir con este filtro");
                  return;
                }
                if (rows.length > 20) {
                  message.warning(
                    `Abrirá solo los primeros 20 resultados (de ${rows.length}).`
                  );
                }
                toOpen.forEach((r: any, i: number) => {
                  const url = `https://legal.5bc.us/api/documents/download/${r.source_document_id}`;
                  setTimeout(() => window.open(url, "_blank"), i * 180);
                });
              }}
            >
              Abrir todos los índices
            </Button>
          </Space>
        </div>
        <Table
          rowKey="id"
          loading={db1995Loading}
          dataSource={getFilteredDb1995Rows()}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ["20", "50", "100", "200"] }}
          scroll={{ x: 2600 }}
          columns={[
            { title: "Caso ID", dataIndex: "case_id", key: "case_id", width: 90 },
            { title: "Archivo", dataIndex: "original_file_name", key: "original_file_name" },
            { title: "Grupo", dataIndex: "group_key", key: "group_key" },
            {
              title: "View",
              key: "view",
              render: (_: any, row: any) => (
                <a
                  href={`https://legal.5bc.us/api/documents/download/${row.source_document_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </a>
              ),
            },
            {
              title: "Indice",
              key: "index_name",
              render: (_: any, row: any) => (
                <Input
                  value={db1995Drafts[row.id]?.index_name ?? ""}
                  onChange={(e) =>
                    setDb1995Drafts((prev) => ({
                      ...prev,
                      [row.id]: {
                        index_name: e.target.value,
                        reference_title: prev[row.id]?.reference_title ?? "",
                      },
                    }))
                  }
                />
              ),
            },
            {
              title: "Reference title",
              key: "reference_title",
              render: (_: any, row: any) => (
                <Input
                  value={db1995Drafts[row.id]?.reference_title ?? ""}
                  onChange={(e) =>
                    setDb1995Drafts((prev) => ({
                      ...prev,
                      [row.id]: {
                        index_name: prev[row.id]?.index_name ?? "",
                        reference_title: e.target.value,
                      },
                    }))
                  }
                />
              ),
            },
            {
              title: "Save",
              key: "save",
              render: (_: any, row: any) => (
                <Button
                  type="primary"
                  size="small"
                  loading={savingDb1995Id === row.id}
                  onClick={async () => {
                    setSavingDb1995Id(row.id);
                    try {
                      const draft = db1995Drafts[row.id] || {
                        index_name: "",
                        reference_title: "",
                      };
                      await db1995API.update(row.id, {
                        index_name: draft.index_name,
                        reference_title: draft.reference_title,
                      });
                      message.success("Registro actualizado");
                      await loadDb1995Rows();
                    } catch (e: any) {
                      message.error(
                        e?.response?.data?.detail || "Error al guardar cambios"
                      );
                    } finally {
                      setSavingDb1995Id(null);
                    }
                  }}
                >
                  Save
                </Button>
              ),
            },
            { title: "Estado", dataIndex: "status", key: "status" },
          ]}
        />
      </Modal>
    </div>
  );
};

export default Dashboard;
